import { User, MeetingRoom, Booking } from '../types/models';
import { logFirebaseEvent } from './firebase';

// 이벤트 카테고리 정의
export enum EventCategory {
  AUTH = 'auth',
  BOOKING = 'booking',
  ROOM = 'room'
}

// 이벤트 액션 정의
export enum EventAction {
  // 인증 관련 액션
  LOGIN = 'login',
  LOGOUT = 'logout',
  SIGNUP = 'signup',
  PASSWORD_RESET = 'password_reset',
  
  // 예약 관련 액션
  CREATE_BOOKING = 'create_booking',
  UPDATE_BOOKING = 'update_booking',
  DELETE_BOOKING = 'delete_booking',
  VIEW_BOOKING = 'view_booking',
  VIEW_MY_BOOKINGS = 'view_my_bookings',
  BOOKING_CONFLICT = 'booking_conflict',
  ENABLE_REMINDER = 'enable_reminder',
  DISABLE_REMINDER = 'disable_reminder',
  SET_RECURRENCE = 'set_recurrence',
  
  // 회의실 관련 액션
  SELECT_ROOM = 'select_room',
  VIEW_ROOM_DETAILS = 'view_room_details'
}

// Firebase Analytics 초기화 함수
export const initializeAnalytics = () => {
  // Firebase는 이미 firebase.ts에서 초기화됨
  console.log('Analytics initialized via Firebase');
};

// 페이지 뷰 이벤트 추적
export const trackPageView = (pagePath: string, pageTitle: string) => {
  logFirebaseEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle
  });
};

// 일반 이벤트 추적
export const trackEvent = (
  category: EventCategory,
  action: EventAction,
  label?: string,
  value?: number,
  additionalParams?: Record<string, any>
) => {
  // 이벤트 이름 형식: category_action (예: booking_create_booking)
  const eventName = `${category}_${action}`;
  
  // 이벤트 파라미터 구성
  const eventParams: Record<string, any> = {
    event_category: category
  };
  
  if (label) {
    eventParams.event_label = label;
  }
  
  if (value !== undefined) {
    eventParams.value = value;
  }
  
  if (additionalParams) {
    Object.assign(eventParams, additionalParams);
  }
  
  // Firebase Analytics 이벤트 로깅
  logFirebaseEvent(eventName, eventParams);
};

// 사용자 속성 설정
export const setUserProperties = (user: User | null) => {
  if (typeof window !== 'undefined' && window.gtag && user) {
    window.gtag('set', 'user_properties', {
      user_id: user.id,
      user_email: user.email,
      user_name: user.name || user.display_name || '이름 없음',
      user_display_name: user.display_name || user.name || '이름 없음',
      user_role: user.role || 'user',
      user_team: user.team || 'none',
      user_position: user.position || '직책 없음',
      user_is_active: user.is_active !== undefined ? user.is_active : true,
      user_email_domain: user.email ? user.email.split('@')[1] : 'unknown',
    });
  }
};

// 예약 생성 이벤트 추적
export const trackBookingCreation = (booking: Booking) => {
  trackEvent(
    EventCategory.BOOKING,
    EventAction.CREATE_BOOKING,
    booking.roomName,
    undefined,
    {
      booking_id: booking.id,
      room_id: booking.roomId,
      room_name: booking.roomName,
      booking_title: booking.title,
      booking_description: booking.description || '설명 없음',
      start_time: booking.startTime instanceof Date ? booking.startTime.toISOString() : new Date(booking.startTime).toISOString(),
      end_time: booking.endTime instanceof Date ? booking.endTime.toISOString() : new Date(booking.endTime).toISOString(),
      duration_minutes: Math.round((new Date(booking.endTime).getTime() - new Date(booking.startTime).getTime()) / 60000),
      has_recurrence: !!booking.recurrence_type,
      recurrence_type: booking.recurrence_type || 'none',
      recurrence_end_date: booking.recurrence_end_date ? (booking.recurrence_end_date instanceof Date ? booking.recurrence_end_date.toISOString() : new Date(booking.recurrence_end_date).toISOString()) : null,
      has_reminder: booking.is_reminder_enabled,
      attendees_count: booking.attendee_uids?.length || 0,
      attendee_uids: booking.attendee_uids ? JSON.stringify(booking.attendee_uids) : '[]',
      user_id: booking.userId,
      day_of_week: new Date(booking.startTime).getDay(),
      hour_of_day: new Date(booking.startTime).getHours(),
      creation_time: new Date().toISOString(),
    }
  );
};

// 예약 수정 이벤트 추적
export const trackBookingUpdate = (booking: Booking, previousBooking: Booking) => {
  const startTimeDiff = new Date(booking.startTime).getTime() - new Date(previousBooking.startTime).getTime();
  const endTimeDiff = new Date(booking.endTime).getTime() - new Date(previousBooking.endTime).getTime();
  const durationChange = (new Date(booking.endTime).getTime() - new Date(booking.startTime).getTime()) - 
                         (new Date(previousBooking.endTime).getTime() - new Date(previousBooking.startTime).getTime());
  
  trackEvent(
    EventCategory.BOOKING,
    EventAction.UPDATE_BOOKING,
    booking.roomName,
    undefined,
    {
      booking_id: booking.id,
      room_name: booking.roomName,
      changed_title: booking.title !== previousBooking.title,
      changed_room: booking.roomName !== previousBooking.roomName,
      changed_start_time: startTimeDiff !== 0,
      changed_end_time: endTimeDiff !== 0,
      changed_duration: durationChange !== 0,
      duration_change_minutes: Math.round(durationChange / 60000),
      changed_recurrence: booking.recurrence_type !== previousBooking.recurrence_type,
      changed_reminder: booking.is_reminder_enabled !== previousBooking.is_reminder_enabled,
      changed_attendees: JSON.stringify(booking.attendee_uids) !== JSON.stringify(previousBooking.attendee_uids),
    }
  );
};

// 예약 삭제 이벤트 추적
export const trackBookingDeletion = (booking: Booking, recurrenceDeleteType?: string) => {
  trackEvent(
    EventCategory.BOOKING,
    EventAction.DELETE_BOOKING,
    booking.roomName,
    undefined,
    {
      booking_id: booking.id,
      room_name: booking.roomName,
      was_recurring: !!booking.recurrence_type,
      recurrence_delete_type: recurrenceDeleteType || 'single',
      day_of_week: new Date(booking.startTime).getDay(),
      hour_of_day: new Date(booking.startTime).getHours(),
      duration_minutes: Math.round((new Date(booking.endTime).getTime() - new Date(booking.startTime).getTime()) / 60000),
    }
  );
};

// 예약 충돌 이벤트 추적
export const trackBookingConflict = (booking: Booking, conflictingBookings: Booking[]) => {
  trackEvent(
    EventCategory.BOOKING,
    EventAction.BOOKING_CONFLICT,
    booking.roomName,
    conflictingBookings.length,
    {
      booking_id: booking.id,
      room_name: booking.roomName,
      conflict_count: conflictingBookings.length,
      conflict_ids: conflictingBookings.map(b => b.id).join(','),
      is_recurring: !!booking.recurrence_type,
    }
  );
};

// 회의실 선택 이벤트 추적
export const trackRoomSelection = (room: MeetingRoom, user: User | null) => {
  trackEvent(
    EventCategory.ROOM,
    EventAction.SELECT_ROOM,
    room.name,
    undefined,
    {
      room_id: room.id,
      room_name: room.name,
      room_capacity: room.capacity,
      room_location: room.location,
      room_description: room.description || '설명 없음',
      room_color: room.color,
      room_has_image: !!room.image_url,
      room_has_equipment: !!room.equipment && room.equipment.length > 0,
      room_equipment: room.equipment || '',
      user_id: user?.id || 'anonymous',
      user_email: user?.email || 'anonymous',
      user_name: user?.name || user?.display_name || 'anonymous',
      user_team: user?.team || 'none',
      selection_time: new Date().toISOString(),
    }
  );
};

// 로그인 이벤트 추적
export const trackLogin = (user: User, method: string) => {
  trackEvent(
    EventCategory.AUTH,
    EventAction.LOGIN,
    method,
    undefined,
    {
      user_id: user.id,
      user_email: user.email,
      user_name: user.name || user.display_name || '이름 없음',
      user_display_name: user.display_name || user.name || '이름 없음',
      user_team: user.team || 'none',
      user_position: user.position || '직책 없음',
      user_role: user.role || 'user',
      user_is_active: user.is_active !== undefined ? user.is_active : true,
      login_method: method,
      login_time: new Date().toISOString(),
      login_day_of_week: new Date().getDay(),
      login_hour: new Date().getHours(),
    }
  );
  
  // 사용자 속성 설정
  setUserProperties(user);
};

// 로그아웃 이벤트 추적
export const trackLogout = (user: User) => {
  trackEvent(
    EventCategory.AUTH,
    EventAction.LOGOUT,
    user.email,
    undefined,
    {
      user_id: user.id,
      user_email: user.email,
      user_name: user.name || user.display_name || '이름 없음',
      user_display_name: user.display_name || user.name || '이름 없음',
      user_team: user.team || 'none',
      user_position: user.position || '직책 없음',
      user_role: user.role || 'user',
      logout_time: new Date().toISOString(),
      logout_day_of_week: new Date().getDay(),
      logout_hour: new Date().getHours(),
      session_duration_minutes: 0, // 세션 지속 시간은 실제 구현 시 계산 필요
    }
  );
};

// 타입 선언 추가
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// 회원가입 이벤트 추적
export const trackSignup = (user: User, method: string) => {
  trackEvent(
    EventCategory.AUTH,
    EventAction.SIGNUP,
    method,
    undefined,
    {
      user_id: user.id,
      user_email: user.email,
      user_name: user.name || user.display_name || '이름 없음',
      user_display_name: user.display_name || user.name || '이름 없음',
      user_team: user.team || 'none',
      user_position: user.position || '직책 없음',
      user_role: user.role || 'user',
      signup_method: method,
      signup_time: new Date().toISOString(),
      signup_day_of_week: new Date().getDay(),
      signup_hour: new Date().getHours(),
    }
  );
  
  // 사용자 속성 설정
  setUserProperties(user);
};

// 비밀번호 재설정 이벤트 추적
export const trackPasswordReset = (email: string) => {
  trackEvent(
    EventCategory.AUTH,
    EventAction.PASSWORD_RESET,
    email,
    undefined,
    {
      user_email: email,
      reset_time: new Date().toISOString(),
      reset_day_of_week: new Date().getDay(),
      reset_hour: new Date().getHours(),
    }
  );
};

// 예약 조회 이벤트 추적
export const trackViewBooking = (booking: Booking, user: User) => {
  trackEvent(
    EventCategory.BOOKING,
    EventAction.VIEW_BOOKING,
    booking.title,
    undefined,
    {
      booking_id: booking.id,
      room_id: booking.roomId,
      room_name: booking.roomName,
      booking_title: booking.title,
      start_time: booking.startTime instanceof Date ? booking.startTime.toISOString() : new Date(booking.startTime).toISOString(),
      end_time: booking.endTime instanceof Date ? booking.endTime.toISOString() : new Date(booking.endTime).toISOString(),
      user_id: user.id,
      user_email: user.email,
      user_name: user.name || user.display_name || '이름 없음',
      user_team: user.team || 'none',
      is_owner: booking.userId === user.id,
      view_time: new Date().toISOString(),
    }
  );
};

// 내 예약 보기 이벤트 추적
export const trackViewMyBookings = (user: User, bookingsCount: number) => {
  trackEvent(
    EventCategory.BOOKING,
    EventAction.VIEW_MY_BOOKINGS,
    user.email,
    undefined,
    {
      user_id: user.id,
      user_email: user.email,
      user_name: user.name || user.display_name || '이름 없음',
      user_team: user.team || 'none',
      user_position: user.position || '직책 없음',
      bookings_count: bookingsCount,
      view_time: new Date().toISOString(),
      view_day_of_week: new Date().getDay(),
      view_hour: new Date().getHours(),
    }
  );
};

// 리마인더 토글 이벤트 추적
export const trackReminderToggle = (booking: Booking, user: User, isEnabled: boolean) => {
  trackEvent(
    EventCategory.BOOKING,
    isEnabled ? EventAction.ENABLE_REMINDER : EventAction.DISABLE_REMINDER,
    booking.title,
    undefined,
    {
      booking_id: booking.id,
      room_id: booking.roomId,
      room_name: booking.roomName,
      booking_title: booking.title,
      start_time: booking.startTime instanceof Date ? booking.startTime.toISOString() : new Date(booking.startTime).toISOString(),
      end_time: booking.endTime instanceof Date ? booking.endTime.toISOString() : new Date(booking.endTime).toISOString(),
      user_id: user.id,
      user_email: user.email,
      user_name: user.name || user.display_name || '이름 없음',
      user_team: user.team || 'none',
      user_position: user.position || '직책 없음',
      user_role: user.role || 'user',
      is_owner: booking.userId === user.id,
      is_reminder_enabled: isEnabled,
      toggle_time: new Date().toISOString(),
      day_of_week: new Date().getDay(),
      hour_of_day: new Date().getHours(),
    }
  );
};

// 반복 예약 설정 이벤트 추적
export const trackRecurrenceSet = (booking: Booking, user: User, recurrenceType: string, endDate: Date | null) => {
  trackEvent(
    EventCategory.BOOKING,
    EventAction.SET_RECURRENCE,
    booking.title,
    undefined,
    {
      booking_id: booking.id,
      room_id: booking.roomId,
      room_name: booking.roomName,
      booking_title: booking.title,
      start_time: booking.startTime instanceof Date ? booking.startTime.toISOString() : new Date(booking.startTime).toISOString(),
      end_time: booking.endTime instanceof Date ? booking.endTime.toISOString() : new Date(booking.endTime).toISOString(),
      user_id: user.id,
      user_email: user.email,
      user_name: user.name || user.display_name || '이름 없음',
      user_team: user.team || 'none',
      user_position: user.position || '직책 없음',
      user_role: user.role || 'user',
      is_owner: booking.userId === user.id,
      recurrence_type: recurrenceType,
      has_end_date: endDate !== null,
      recurrence_end_date: endDate ? endDate.toISOString() : null,
      set_time: new Date().toISOString(),
      day_of_week: new Date().getDay(),
      hour_of_day: new Date().getHours(),
    }
  );
};

const analyticsService = {
  initializeAnalytics,
  trackPageView,
  trackEvent,
  setUserProperties,
  trackBookingCreation,
  trackBookingUpdate,
  trackBookingDeletion,
  trackBookingConflict,
  trackRoomSelection,
  trackLogin,
  trackLogout,
  trackSignup,
  trackPasswordReset,
  trackViewBooking,
  trackViewMyBookings,
  trackReminderToggle,
  trackRecurrenceSet
};

export default analyticsService;
